"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";

interface Props {
  text: string;
  /** Approx character count to show before truncating. Default ~420 chars. */
  charLimit?: number;
}

/** Collapsed/expanded long-text block used by the listing description. */
export default function ExpandableText({ text, charLimit = 420 }: Props) {
  const t = useTranslations("expandableText");
  const [expanded, setExpanded] = useState(false);
  const needsToggle = text.length > charLimit;

  // When collapsed, cut at the nearest sentence boundary to avoid mid-word
  // truncation. Falls back to the raw character cut if no sentence ends within
  // the window.
  const collapsed = (() => {
    if (!needsToggle) return text;
    const slice = text.slice(0, charLimit);
    const lastBreak = Math.max(
      slice.lastIndexOf(". "),
      slice.lastIndexOf("?\n"),
      slice.lastIndexOf("!\n"),
      slice.lastIndexOf("\n\n"),
    );
    if (lastBreak > charLimit * 0.6) return slice.slice(0, lastBreak + 1);
    return slice.trimEnd() + "…";
  })();

  return (
    <div className="text-ss-muted text-[15px] leading-relaxed whitespace-pre-line max-w-prose">
      {expanded || !needsToggle ? text : collapsed}
      {needsToggle && (
        <button
          type="button"
          onClick={() => setExpanded((s) => !s)}
          className="block mt-2 text-ss-blue font-semibold hover:underline"
        >
          {expanded ? t("seeLess") : t("seeMore")}
        </button>
      )}
    </div>
  );
}
